import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * The page sizes every recruiter list offers. 20 stays the default so an
 * existing recruiter's first page is unchanged; 50 and 100 exist because a
 * selection is a unit of work and repeating it five times to act on a hundred
 * candidates is the friction this control removes.
 */
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_PAGE_SIZE: PageSizeOption = 20;

/** Narrow an arbitrary number (a persisted preference, a URL param) to an offered size. */
export function coercePageSize(value: unknown): PageSizeOption {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(parsed)
    ? (parsed as PageSizeOption)
    : DEFAULT_PAGE_SIZE;
}

/**
 * TablePagination — the standard pagination row under a DataTable.
 * Deliberately dumb: 1-based `page`, `pageCount`, `onPageChange` — works the
 * same for server-driven lists (candidates, calls) and client-sliced ones.
 */
export interface TablePaginationProps {
  /** 1-based current page. */
  page: number;
  /**
   * Exact page count. null/undefined means the aggregate was not requested.
   *
   * A non-positive value is treated the same way. A list that has rows on
   * screen does not have zero pages, so 0 is a count that was never taken, not
   * a count of nothing — and trusting it would hide the pager on a list the
   * `hasNextPage` lane knows continues.
   */
  pageCount?: number | null;
  /** Cursor-lane next-page signal used while pageCount is unknown. */
  hasNextPage?: boolean;
  /** Total row count — shown as "(N total)" when provided. */
  total?: number;
  /**
   * Noun for `total`, when "total" is not what the list steps through.
   * Applications pages by CANDIDATE while showing an application count
   * alongside, so "(380 candidates)" is the only unambiguous label.
   */
  totalUnit?: string;
  onPageChange: (page: number) => void;
  /** Extra summary text (e.g. selected-count). */
  summary?: React.ReactNode;
  /**
   * Current page size. Passing this together with `onPageSizeChange` renders
   * the rows-per-page selector; omitting either leaves the row exactly as it
   * was for lists that have not adopted it yet.
   */
  pageSize?: number;
  onPageSizeChange?: (pageSize: PageSizeOption) => void;
  className?: string;
}

export function TablePagination({
  page,
  pageCount,
  hasNextPage = false,
  total,
  totalUnit = 'total',
  onPageChange,
  summary,
  pageSize,
  onPageSizeChange,
  className,
}: TablePaginationProps) {
  // Only a positive page count is an answer; see `pageCount` above.
  const hasExactPageCount = pageCount != null && pageCount > 0;
  const normalizedPageCount = hasExactPageCount ? Math.max(pageCount, 1) : null;
  const showPageSize = pageSize != null && onPageSizeChange != null;

  if (
    !summary
    && !showPageSize
    && page <= 1
    && (hasExactPageCount ? normalizedPageCount! <= 1 : !hasNextPage)
  ) return null;

  const nextDisabled = hasExactPageCount
    ? page >= normalizedPageCount!
    : !hasNextPage;

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground', className)}
    >
      <span aria-live="polite">
        {hasExactPageCount
          ? `Page ${page} of ${normalizedPageCount}`
          : `Page ${page}${hasNextPage ? ', more available' : ''}`}
        {total != null ? ` (${total.toLocaleString()} ${totalUnit})` : ''}
        {summary ? <> · {summary}</> : null}
      </span>
      <div className="flex items-center gap-2">
        {showPageSize && (
          <div className="flex items-center gap-2">
            <label htmlFor="table-page-size" className="whitespace-nowrap">
              Rows per page
            </label>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(coercePageSize(value))}
            >
              <SelectTrigger id="table-page-size" className="h-8 w-[72px]" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={nextDisabled}
            onClick={() => onPageChange(page + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </nav>
  );
}
